/**
 * Sentry noise filters.
 *
 * Two layers keep the free-tier error quota for real bugs:
 *  - `ignoreErrors`: well-known benign messages (browser quirks, extension
 *    chatter, network blips) that are never actionable.
 *  - `beforeSend`: drops any event whose stack originates in a browser
 *    extension — these are not our code and we cannot fix them.
 *
 * Kept pure and dependency-free so it is unit-testable in isolation.
 */
import type { ErrorEvent } from '@sentry/react'

/** Substrings/patterns of error messages that are benign browser/network noise. */
export const ignoreErrors: (string | RegExp)[] = [
  // ResizeObserver loop notifications — benign, fired by many UI libraries.
  'ResizeObserver loop limit exceeded',
  'ResizeObserver loop completed with undelivered notifications',
  // Generic network failures (offline, cancelled navigations) — not app bugs.
  'Failed to fetch',
  'NetworkError when attempting to fetch resource',
  'Load failed',
  'The operation was aborted',
  'AbortError',
  // Safari/extension non-errors.
  'Non-Error promise rejection captured',
  // Chunk load failures after a deploy (user has a stale tab) — transient.
  /Loading chunk \d+ failed/,
  /Importing a module script failed/,
]

const EXTENSION_PROTOCOLS = ['chrome-extension://', 'moz-extension://', 'safari-web-extension://']

/**
 * Meta's in-app browser (Facebook/Instagram webviews) injects a bridge script
 * served from this scheme. It registers its own `beforeunload` listener to
 * notify the native shell, and Sentry's browserApiErrors integration — which
 * patches `addEventListener` page-wide, not just for our handlers — captures
 * the exception thrown when that native object is torn down on navigation.
 * We register no unload handler anywhere, so no frame here is ever ours.
 */
const IN_APP_BROWSER_PROTOCOLS = ['iabjs://']

/**
 * Returns true if any stack frame's filename matches one of `protocols`.
 */
function hasFrameFrom(event: ErrorEvent, protocols: string[]): boolean {
  const values = event.exception?.values
  if (!values) return false
  for (const value of values) {
    const frames = value.stacktrace?.frames
    if (!frames) continue
    for (const frame of frames) {
      const filename = frame.filename ?? ''
      if (protocols.some((proto) => filename.includes(proto))) {
        return true
      }
    }
  }
  return false
}

/**
 * Returns true if any stack frame comes from a browser extension.
 */
function isFromBrowserExtension(event: ErrorEvent): boolean {
  return hasFrameFrom(event, EXTENSION_PROTOCOLS)
}

/**
 * Returns true if any stack frame comes from an in-app browser's injected bridge.
 *
 * Deliberately keyed on the frame, not the message: a bare /postMessage/ message
 * pattern would also swallow a real postMessage bug originating in our own code.
 */
function isFromInAppBrowserBridge(event: ErrorEvent): boolean {
  return hasFrameFrom(event, IN_APP_BROWSER_PROTOCOLS)
}

/**
 * Sentry `beforeSend` hook. Drops events originating in a browser extension or
 * an in-app browser's injected bridge; passes everything else through unchanged.
 */
export function beforeSend(event: ErrorEvent): ErrorEvent | null {
  if (isFromBrowserExtension(event) || isFromInAppBrowserBridge(event)) {
    return null
  }
  return event
}
